import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { WhepClient } from '../utils/whepClient';
import { AudioPlayback } from '../utils/audioPlayback';
import { apiClient } from '../api/client';
import { config } from '../config';

interface LocationState {
  sessionName?: string;
  hostName?: string;
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'ended' | 'error';

export default function Listen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  const [sessionName] = useState(state?.sessionName || 'Unknown Session');
  const [hostName] = useState(state?.hostName || 'Unknown Host');
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const [volume, setVolume] = useState(0.8);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const whepClientRef = useRef<WhepClient | null>(null);
  const audioPlaybackRef = useRef<AudioPlayback | null>(null);

  /**
   * Initialize WebRTC connection and WHEP client
   */
  useEffect(() => {
    if (!sessionId) {
      setErrorMessage('Invalid session ID');
      setConnectionStatus('error');
      return;
    }

    const initializeConnection = async () => {
      try {
        // Get auth token
        const authToken = apiClient.getAuthToken();
        if (!authToken) {
          setErrorMessage('Authentication required');
          setConnectionStatus('error');
          // Navigate to login with redirect back to this page
          navigate('/', {
            state: { from: `${location.pathname}${location.search}` },
          });
          return;
        }

        // Create WHEP client
        const whepClient = new WhepClient({
          sessionId,
          apiUrl: config.apiUrl,
          authToken,
          enableAutoReconnect: true,
          maxReconnectAttempts: 3,
        });

        whepClientRef.current = whepClient;

        // Create audio playback handler
        const audioPlayback = new AudioPlayback({
          autoplay: true,
        });

        audioPlaybackRef.current = audioPlayback;
        
        // Set initial volume
        audioPlayback.setVolume(volume);

        // Set up connection state callback
        whepClient.onStateChange((state) => {
          console.log('Connection state:', state);
          
          if (state.state === 'connected') {
            setConnectionStatus('connected');
            setErrorMessage('');
          } else if (state.state === 'disconnected' || state.state === 'failed') {
            if (state.reconnecting) {
              setConnectionStatus('connecting');
              setErrorMessage(`Reconnecting... (attempt ${state.reconnectAttempt})`);
            } else {
              setConnectionStatus('disconnected');
              setErrorMessage('Connection lost');
            }
          } else if (state.state === 'closed') {
            // Session has ended
            setConnectionStatus('ended');
            setErrorMessage('');
          }
        });

        // Set up error callback
        whepClient.onError((error) => {
          console.error('WHEP client error:', error);
          
          // Check if session has ended
          if (error.message.includes('session ended') || error.message.includes('404')) {
            setConnectionStatus('ended');
            setErrorMessage('');
          } else if (error.message.includes('No audio tracks available') || error.message.includes('not be streaming yet')) {
            // Host hasn't started streaming yet
            setErrorMessage('The host is not streaming yet. Please wait a moment and try again.');
            setConnectionStatus('error');
          } else {
            setErrorMessage(error.message);
            setConnectionStatus('error');
          }
        });

        // Set up stream callback
        whepClient.onStream(async (stream) => {
          console.log('Received audio stream');
          
          if (audioRef.current) {
            try {
              await audioPlayback.connectStream(stream, audioRef.current);
              console.log('Audio playback started');
            } catch (error) {
              console.error('Audio playback error:', error);
              
              // Check if it's an autoplay error
              if (error instanceof Error && error.message.includes('Autoplay blocked')) {
                setAutoplayBlocked(true);
                setErrorMessage('Click "Start Playback" to begin listening');
              } else {
                setErrorMessage(error instanceof Error ? error.message : 'Failed to start audio playback');
              }
            }
          }
        });

        // Connect to session
        setConnectionStatus('connecting');
        await whepClient.connect();

      } catch (error) {
        console.error('Failed to initialize connection:', error);
        setErrorMessage(error instanceof Error ? error.message : 'Failed to connect to session');
        setConnectionStatus('error');
      }
    };

    initializeConnection();

    return () => {
      // Cleanup on unmount
      if (whepClientRef.current) {
        whepClientRef.current.disconnect();
        whepClientRef.current = null;
      }
      if (audioPlaybackRef.current) {
        audioPlaybackRef.current.cleanup();
        audioPlaybackRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, navigate]);

  /**
   * Handle volume changes
   */
  useEffect(() => {
    if (audioPlaybackRef.current) {
      audioPlaybackRef.current.setVolume(volume);
    }
  }, [volume]);

  /**
   * Handle manual playback start (for autoplay blocked scenarios)
   */
  const handleStartPlayback = async () => {
    if (audioPlaybackRef.current) {
      try {
        await audioPlaybackRef.current.startPlayback();
        setAutoplayBlocked(false);
        setErrorMessage('');
      } catch (error) {
        console.error('Failed to start playback:', error);
        setErrorMessage(error instanceof Error ? error.message : 'Failed to start playback');
      }
    }
  };

  /**
   * Handle leave session
   */
  const handleLeaveSession = () => {
    // Cleanup connections
    if (whepClientRef.current) {
      whepClientRef.current.disconnect();
      whepClientRef.current = null;
    }
    if (audioPlaybackRef.current) {
      audioPlaybackRef.current.cleanup();
      audioPlaybackRef.current = null;
    }

    // Navigate back to join page
    navigate('/join');
  };

  /**
   * Get status indicator class
   */
  const getStatusClass = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'status-connected';
      case 'connecting':
        return 'status-connecting';
      case 'disconnected':
      case 'error':
        return 'status-error';
      case 'ended':
        return 'status-ended';
      default:
        return '';
    }
  };

  /**
   * Get status text
   */
  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'disconnected':
        return 'Disconnected';
      case 'error':
        return 'Connection Error';
      case 'ended':
        return 'Session Ended';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="listen-container">
      <header className="listen-header">
        <h1>Silent Disco - Listener</h1>
        <div className={`connection-status ${getStatusClass()}`}>
          <span className="status-indicator"></span>
          <span className="status-text">{getStatusText()}</span>
        </div>
      </header>

      <main className="listen-content">
        <div className="session-info">
          <h2>{sessionName}</h2>
          <p className="host-name">Hosted by {hostName}</p>
        </div>

        {errorMessage && !autoplayBlocked && (
          <div className="error-message" role="alert">
            {errorMessage}
          </div>
        )}

        {autoplayBlocked && (
          <div className="info-message" role="status">
            <p>Audio is ready to play</p>
            <button
              onClick={handleStartPlayback}
              className="start-playback-button"
            >
              Start Playback
            </button>
          </div>
        )}

        {connectionStatus === 'ended' && (
          <div className="session-ended-notification" role="status">
            <div className="ended-icon">🎵</div>
            <h3>Session Ended</h3>
            <p>This streaming session has ended.</p>
            <p>Thank you for listening!</p>
            <button
              onClick={() => navigate('/join')}
              className="primary-button"
            >
              Join Another Session
            </button>
          </div>
        )}

        {connectionStatus === 'connected' && !autoplayBlocked && (
          <div className="audio-controls">
            <div className="volume-control">
              <label htmlFor="volume-slider">
                <span className="volume-icon">🔊</span>
                Volume
              </label>
              <input
                id="volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                aria-label="Volume control"
              />
              <span className="volume-value">{Math.round(volume * 100)}%</span>
            </div>
          </div>
        )}

        {(connectionStatus === 'disconnected' || connectionStatus === 'error') && (
          <div className="reconnection-info">
            <p>Connection lost. Attempting to reconnect...</p>
            <button
              onClick={() => window.location.reload()}
              className="retry-button"
            >
              Retry Connection
            </button>
          </div>
        )}

        <div className="action-buttons">
          <button
            onClick={handleLeaveSession}
            className="leave-button"
            disabled={connectionStatus === 'connecting'}
          >
            Leave Session
          </button>
        </div>
      </main>

      {/* Hidden audio element for playback */}
      <audio
        ref={audioRef}
        autoPlay
        playsInline
        style={{ display: 'none' }}
      />
    </div>
  );
}
